import { format } from "date-fns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LimitEditor } from "./LimitEditor";

const when = (ms) => (ms ? format(new Date(ms), "d MMM yyyy") : "Never");

const Person = ({ u, isMe }) => (
  <div className="flex items-center gap-3">
    {u.image_url
      ? <img src={u.image_url} alt="" className="h-9 w-9 shrink-0 rounded-full border object-cover" />
      : <span className="h-9 w-9 shrink-0 rounded-full bg-muted" />}
    <div className="min-w-0">
      <p className="truncate font-semibold" data-testid={`admin-user-name-${u.user_id}`}>{u.name || u.email || "No name"}{isMe && <span className="ml-1.5 text-xs font-normal text-muted-foreground">(you)</span>}</p>
      <p className="truncate text-xs text-muted-foreground" data-testid={`admin-user-email-${u.user_id}`}>
        {u.email}{u.google && <span className="ml-1.5 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-secondary-foreground">Google</span>}
      </p>
    </div>
  </div>
);

const Usage = ({ u }) => {
  const full = u.project_count >= u.project_limit;
  const pct = u.project_limit ? Math.min(100, (u.project_count / u.project_limit) * 100) : 100;
  return (
    <div className="w-28">
      <p className="text-sm font-semibold" data-testid={`admin-user-projects-${u.user_id}`}>
        {u.project_count} <span className="font-normal text-muted-foreground">of {u.project_limit}</span>
      </p>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className={`h-full rounded-full ${full ? "bg-accent" : "bg-primary"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const RoleSelect = ({ u, roles, onSetRole }) => (
  <Select value={u.role} onValueChange={(r) => r !== u.role && onSetRole(u, r)}>
    <SelectTrigger className="h-9 w-32 rounded-full" data-testid={`admin-role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
    <SelectContent>
      {roles.map((r) => <SelectItem key={r} value={r} data-testid={`admin-role-option-${r}`} className="capitalize">{r === "admin" ? "Admin" : "Member"}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const UsersTable = ({ users, roles, meId, onSetLimit, onSetRole }) => (
  <div className="mt-4 overflow-x-auto rounded-2xl border bg-card" data-testid="admin-users-table">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Person</TableHead>
          <TableHead>Projects</TableHead>
          <TableHead>Project limit</TableHead>
          <TableHead>Role</TableHead>
          <TableHead className="hidden lg:table-cell">Joined</TableHead>
          <TableHead className="hidden lg:table-cell">Last signed in</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.length === 0 && (
          <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground" data-testid="admin-users-empty">No users match that search</TableCell></TableRow>
        )}
        {users.map((u) => (
          <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
            <TableCell className="min-w-[240px]"><Person u={u} isMe={u.user_id === meId} /></TableCell>
            <TableCell><Usage u={u} /></TableCell>
            <TableCell><LimitEditor u={u} onSave={(n) => onSetLimit(u, n)} /></TableCell>
            <TableCell><RoleSelect u={u} roles={roles} onSetRole={onSetRole} /></TableCell>
            <TableCell className="hidden whitespace-nowrap text-sm text-muted-foreground lg:table-cell">{when(u.created_at)}</TableCell>
            <TableCell className="hidden whitespace-nowrap text-sm text-muted-foreground lg:table-cell">{when(u.last_sign_in_at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);
